/**
 * La única puerta de salida del cliente hacia el servidor — M7-T02 (UC-07.02).
 *
 * QUÉ HACE
 *   Pide JSON a `/api` **con la cookie de sesión**, y convierte cualquier final que no sea un 2xx
 *   en un `ApiError` con el cuerpo de la sección 9.3 (`{ error, code?, details? }`). Nadie más
 *   llama a `fetch`: si aparece un `fetch` en una pantalla, es que algo se ha saltado esta capa.
 *
 * POR QUÉ EL 401 REDIRIGE AQUÍ Y NO EN CADA PANTALLA
 *   La sesión se pierde una vez, pero se nota en todas las peticiones que hubiera en vuelo. Si
 *   cada pantalla reaccionara por su cuenta, ocho consultas caídas serían ocho navegaciones. El
 *   cerrojo `yaRedirigido` deja pasar la primera y calla el resto (aceptación de M7-T02).
 *
 * POR QUÉ LA REDIRECCIÓN ES UNA NAVEGACIÓN DE VERDAD Y NO UNA DEL ENRUTADOR
 *   Porque al perder la sesión sobra **todo** lo que hay en memoria: la caché de TanStack Query
 *   guarda la plantilla, el mercado y el plan de una sesión que ya no existe. Recargar la página
 *   los tira de una vez y deja el cliente como recién abierto. `setUnauthorizedRedirect` existe
 *   para que las pruebas puedan mirar esa decisión sin navegar de verdad.
 *
 * EL ACCESO ES LA EXCEPCIÓN
 *   `login()` pide `redirectOnUnauthorized: false`: su 401 significa «esa contraseña no es», no
 *   «se ha ido la sesión», y redirigir a `/login` desde `/login` sería un bucle.
 */

import type { ApiErrorBody } from "./types";

export const API_BASE = "/api";
export const LOGIN_PATH = "/login";

/* ============================================================================ errores ===== */

/** Estado que se usa para lo que ni siquiera llegó a haber respuesta (red caída, DNS, offline). */
export const NETWORK_STATUS = 0;

/**
 * Un final que no es un 2xx, con el cuerpo de la sección 9.3 ya leído.
 * `message` es para el registro; lo que se enseña en pantalla sale de `describeError`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly error: string;
  readonly code: string | undefined;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(`${status} ${body.error}${body.code ? ` (${body.code})` : ""}`);
    this.name = "ApiError";
    this.status = status;
    this.error = body.error;
    this.code = body.code;
    this.details = body.details;
  }

  /** `true` cuando la petición no llegó a salir o no volvió: no hay nada que interpretar. */
  get isNetwork(): boolean {
    return this.status === NETWORK_STATUS;
  }
}

/**
 * Qué enseñar cuando algo falla, en español y sin jerga técnica (sección 8.3). Es lo que va al
 * `message` de `ErrorState`, debajo del «No se han podido cargar los datos».
 */
export function describeError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return "Ha ocurrido un problema inesperado.";
  }
  if (error.isNetwork) return "No se ha podido conectar con la aplicación.";
  if (error.code === "biwenger_auth") {
    return "Biwenger ha rechazado las credenciales: el token ha caducado.";
  }
  if (error.code === "sync_running")
    return "Ya hay una sincronización en marcha.";

  switch (error.status) {
    case 400:
      return "Algún dato de la petición no es válido.";
    case 401:
      return "La sesión ha caducado.";
    case 404:
      return "No se ha encontrado lo que buscabas.";
    case 409:
      return "Hay otra operación en curso.";
    case 429:
      return "Demasiados intentos seguidos. Espera un momento.";
    case 502:
      return "Biwenger no ha respondido.";
    default:
      return "La aplicación ha tenido un problema.";
  }
}

/* ============================================================== la sesión que se pierde === */

let irAlAcceso: () => void = () => {
  window.location.assign(LOGIN_PATH);
};
let yaRedirigido = false;

/** Sustituye la navegación por otra cosa. Existe para las pruebas: en la app es la de verdad. */
export function setUnauthorizedRedirect(handler: () => void): void {
  irAlAcceso = handler;
}

/** Vuelve a armar el cerrojo: tras entrar de nuevo, un 401 posterior sí debe redirigir. */
export function resetUnauthorizedRedirect(): void {
  yaRedirigido = false;
}

function perderSesion(): void {
  if (yaRedirigido) return;
  // Ya estoy en el acceso: el 401 de ahí lo cuenta la propia pantalla, junto al campo.
  if (window.location.pathname === LOGIN_PATH) return;
  yaRedirigido = true;
  irAlAcceso();
}

/* ============================================================================ peticiones == */

export type QueryValue = string | number | boolean | undefined | null;
export type QueryParams = Record<string, QueryValue>;

/**
 * Monta la cadena de consulta de la sección 9.1. Un valor ausente o vacío **no se escribe**: es
 * lo que el servidor entiende por «no filtres» (`http/query.ts`). Los interruptores viajan como
 * `0`/`1`, que es como los declara la sección 9.1.
 */
export function buildUrl(path: string, query?: QueryParams): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    search.set(
      name,
      typeof value === "boolean" ? (value ? "1" : "0") : String(value),
    );
  }
  const cadena = search.toString();
  return `${API_BASE}${path}${cadena === "" ? "" : `?${cadena}`}`;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  query?: QueryParams;
  /** Se manda como JSON. `undefined` = petición sin cuerpo. */
  body?: unknown;
  /** El que TanStack Query entrega al `queryFn`: cancela la petición si la consulta se descarta. */
  signal?: AbortSignal;
  /** Solo el acceso lo apaga: su 401 no significa que se haya perdido la sesión. */
  redirectOnUnauthorized?: boolean;
}

const NO_CONTENT = 204;

/**
 * Una petición a la API. Devuelve el JSON ya tipado; lanza `ApiError` en cualquier otro final.
 * El `AbortError` se deja pasar tal cual: no es un fallo, es una consulta que ya no interesa.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    query,
    body,
    signal,
    redirectOnUnauthorized = true,
  } = options;

  let respuesta: Response;
  try {
    respuesta = await fetch(buildUrl(path, query), {
      method,
      // Sin la cookie firmada toda ruta responde 401: la sesión es lo único que autentica.
      credentials: "same-origin",
      headers:
        body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (causa) {
    if (causa instanceof Error && causa.name === "AbortError") throw causa;
    throw new ApiError(NETWORK_STATUS, {
      error: "network",
      details: causa instanceof Error ? causa.message : String(causa),
    });
  }

  if (!respuesta.ok) {
    const cuerpo = await leerCuerpoDeError(respuesta);
    if (respuesta.status === 401 && redirectOnUnauthorized) perderSesion();
    throw new ApiError(respuesta.status, cuerpo);
  }

  // `204` es la respuesta de `POST /api/auth/logout` y de `DELETE /api/plan`: no hay cuerpo que
  // leer, y pedirlo daría un error de análisis en vez del éxito que es.
  if (respuesta.status === NO_CONTENT) return undefined as unknown as T;

  let datos: unknown;
  try {
    datos = await respuesta.json();
  } catch {
    throw new ApiError(respuesta.status, { error: "bad_response" });
  }
  return datos as T;
}

/** Lee `{ error, code?, details? }`. Lo que no lo sea se resume por su código HTTP. */
async function leerCuerpoDeError(respuesta: Response): Promise<ApiErrorBody> {
  try {
    const datos: unknown = await respuesta.json();
    if (esCuerpoDeError(datos)) return datos;
  } catch {
    // Un proxy o el propio navegador pueden contestar HTML: no hay nada más que sacar.
  }
  return { error: `http_${respuesta.status}` };
}

function esCuerpoDeError(value: unknown): value is ApiErrorBody {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { error?: unknown }).error === "string"
  );
}

/* ------------------------------------------------------------------------- atajos -------- */

export function apiGet<T>(
  path: string,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<T> {
  return apiRequest<T>(path, { ...options, method: "GET" });
}

export function apiPost<T>(
  path: string,
  body?: unknown,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<T> {
  return apiRequest<T>(path, { ...options, method: "POST", body });
}

export function apiPut<T>(
  path: string,
  body: unknown,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<T> {
  return apiRequest<T>(path, { ...options, method: "PUT", body });
}

export function apiDelete(
  path: string,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<void> {
  return apiRequest<void>(path, { ...options, method: "DELETE" });
}

/* =============================================================================== acceso == */

export type LoginFailure = "credentials" | "rate-limit" | "network";
export type LoginResult = { ok: true } | { ok: false; reason: LoginFailure };

/**
 * `POST /api/auth/login` (sección 9.1). Devuelve **por qué** ha fallado en vez de lanzar, porque
 * los tres motivos se enseñan en sitios distintos y la pantalla necesita distinguirlos: la
 * contraseña equivocada va junto al campo, y el limitador de intentos es un aviso de pantalla.
 */
export async function login(password: string): Promise<LoginResult> {
  try {
    await apiRequest<void>("/auth/login", {
      method: "POST",
      body: { password },
      redirectOnUnauthorized: false,
    });
    // Sesión nueva: el cerrojo del 401 vuelve a estar armado para cuando esta también caduque.
    resetUnauthorizedRedirect();
    return { ok: true };
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    if (error.isNetwork) return { ok: false, reason: "network" };
    if (error.status === 429) return { ok: false, reason: "rate-limit" };
    return { ok: false, reason: "credentials" };
  }
}
